import { forwardRef, useCallback, useLayoutEffect, useState } from 'react';

import { useMatch, useNavigate } from '@spazious/dollhouse-router';
import { styled } from '@spazious/dollhouse-theme';
import { Flex, FlexElement, FlexProps } from '@spazious/dollhouse-uikit';
import { useEventCallback } from '@spazious/react-hooks';

import { ROUTE_PATH_ROOT } from '../../constants/index.js';
import { useDeviceInfo } from '../../hooks/index.js';
import { SideMenuMobile } from '../SideMenuMobile/index.js';
import { SidePanelMobile } from '../SidePanelMobile/index.js';

const directionMapping = {
  top: 'column',
  right: 'row-reverse',
  bottom: 'column-reverse',
  left: 'row',
} as const;

const styleMapping = {
  top: { width: '100dvw' },
  right: { height: '100dvh' },
  bottom: { width: '100dvw' },
  left: { height: '100dvh' },
} as const;

export type SideBarMobileElement = FlexElement;

export interface SideBarMobileProps extends FlexProps {}

export const SideBarMobile = styled(
  forwardRef<SideBarMobileElement, SideBarMobileProps>(({ children, ...restProps }) => {
    const navigate = useNavigate();
    const matched = useMatch('/');
    const isPortrait = useDeviceInfo((state) => state.isPortrait);
    const [showSideMenu, setShowSideMenu] = useState(true);

    const sideMenuPosition = directionMapping[isPortrait ? 'bottom' : 'left'];

    const handlePanelClose = useCallback(() => {
      navigate(ROUTE_PATH_ROOT);
    }, [navigate]);

    const handleClickShowMenu = useEventCallback(() => {
      setShowSideMenu(!showSideMenu);
      handlePanelClose();
    });

    useLayoutEffect(() => {
      // sync showSideMenu to false in mobile devices only in the first render
      matched && setShowSideMenu(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const style = styleMapping[isPortrait ? 'bottom' : 'left'];

    return (
      <Flex flexDirection={sideMenuPosition} style={style} {...restProps}>
        <SideMenuMobile isPortrait={isPortrait} open={showSideMenu} onClick={handleClickShowMenu} />
        <SidePanelMobile open={!matched} onClose={handlePanelClose}>
          {children}
        </SidePanelMobile>
      </Flex>
    );
  })
).withConfig({
  displayName: 'SideBarMobile',
})`
  align-items: center;
`;
